import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { CreationToolsContext } from '../manager'
import CreationToolGradient from '../types/gradient'

const DropZone = styled.div`
    border-radius: inherit;
    position: relative;
`

const Gradient = styled.div`
    border-radius: inherit;
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;

    &:after {
        background-image: repeating-linear-gradient(0deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(90deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(180deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(270deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px);
        background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;
        background-position: 0 0, 0 0, 100% 0, 0 100%;
        background-repeat: no-repeat;
        border-radius: inherit;
        bottom: -8px;
        content: '';
        left: -8px;
        position: absolute;
        right: -8px;
        top: -8px;
    }
`

type Props = {
    children?: ReactNode
    className?: string
    gradient?: CreationToolGradient
    disabled?: boolean
}

const CreationToolsDropZone = ({
    className,
    children,
    gradient = CreationToolGradient.IndigoPurple,
    disabled = false,
}: Props) => {
    const { isDraggingFile, setIsDraggingFile } = useContext(CreationToolsContext)
    const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false)
    const dragTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null)
    const gradientColors = isDraggedOver
        ? CreationToolGradient.colors(gradient)
        : [`rgba(18, 18, 18, 0.15)`, `rgba(33, 33, 33, 0.15)`]

    const onDragOver = () => {
        if (disabled) return
        setIsDraggedOver(true)
    }

    const onDragOut = () => {
        if (disabled) return
        setIsDraggedOver(false)
    }

    const onDragEnter = (e: DragEvent) => {
        if (disabled) return

        // prevent the browser from opening the file when dropping outside a file input
        if ((e.target as HTMLInputElement).type !== 'file') {
            e.preventDefault()
        }
        dragTimeoutRef.current && clearTimeout(dragTimeoutRef.current)
        setIsDraggingFile(true)
    }

    const onDragLeave = (e: DragEvent) => {
        if (disabled) return

        if ((e.target as HTMLInputElement).type !== 'file') {
            e.preventDefault()
        }
        // @ts-ignore
        dragTimeoutRef.current = setTimeout(() => {
            setIsDraggingFile(false)
        }, 300)
    }

    useEffect(() => {
        setIsDraggingFile(false)
        if (window) {
            window.addEventListener('dragover', onDragEnter)
            window.addEventListener('dragenter', onDragEnter)
            window.addEventListener('dragleave', onDragLeave)
            window.addEventListener('dragend', onDragLeave)
            window.addEventListener('drop', onDragLeave)
        }

        return () => {
            dragTimeoutRef.current && clearTimeout(dragTimeoutRef.current)
            setIsDraggingFile(false)
            if (window) {
                window.removeEventListener('dragover', onDragEnter)
                window.removeEventListener('dragenter', onDragEnter)
                window.removeEventListener('dragleave', onDragLeave)
                window.removeEventListener('dragend', onDragLeave)
                window.removeEventListener('drop', onDragLeave)
            }
        }
    }, [])

    useEffect(() => {
        if (!isDraggingFile) {
            setIsDraggedOver(false)
        }
    }, [isDraggingFile])

    return (
        <DropZone
            {...{
                className,
                style: isDraggingFile ? { zIndex: 11 } : {},
                onDragEnter: onDragOver,
                onDragLeave: onDragOut,
            }}
        >
            {isDraggingFile && (
                <Gradient
                    style={{
                        background: `linear-gradient(45deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
                    }}
                />
            )}
            {children}
        </DropZone>
    )
}

export default CreationToolsDropZone
